import type { TimesheetPageState } from './page-params-state.model';

type TimesheetPeriod = 'day' | 'week' | 'month';
type TimesheetViewMode = 'employee' | 'team' | 'priority';

export type TimesheetLinkCommands = (string | { date: string })[];

/**
 * Builds the router command array for the timesheet, falling back to safe
 * defaults when the persisted period/mode are not recognised. Shared by the
 * side-nav and header nav so both navigate to the same destination.
 *
 * The "Priority" view is a real view mode with day/week/month periods. When it
 * is disabled (labs off) a persisted priority mode is unreachable, so the link
 * falls back to the team view keeping the current period. Two legacy shapes are
 * still honoured: a persisted `mode: 'list'` (from before the view was renamed
 * to "priority") and a persisted `period: 'list'` (from before the view/period
 * split) — the latter read as the priority day view, or the stored
 * employee/team day view when labs is off.
 */
export function buildTimesheetLink(
  { mode, period, date }: TimesheetPageState,
  priorityViewEnabled: boolean,
): TimesheetLinkCommands {
  if (period === 'list') {
    const legacyMode = isEmployeeOrTeam(mode) ? mode : 'team';
    return priorityViewEnabled
      ? ['/timesheet', 'priority', 'day', { date }]
      : ['/timesheet', legacyMode, 'day', { date }];
  }

  const safePeriod = isTimesheetPeriod(period) ? period : 'day';

  if (isPriorityMode(mode)) {
    return priorityViewEnabled
      ? ['/timesheet', 'priority', safePeriod, { date }]
      : ['/timesheet', 'team', safePeriod, { date }];
  }

  const safeMode = isEmployeeOrTeam(mode) ? mode : 'team';
  return ['/timesheet', safeMode, safePeriod, { date }];
}

function isTimesheetPeriod(period: string): period is TimesheetPeriod {
  return period === 'day' || period === 'week' || period === 'month';
}

/** `'list'` is the pre-rename value still sitting in persisted page params. */
function isPriorityMode(mode: string): boolean {
  return mode === 'priority' || mode === 'list';
}

function isEmployeeOrTeam(mode: string): mode is Exclude<TimesheetViewMode, 'priority'> {
  return mode === 'employee' || mode === 'team';
}
